import type { ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import { useAuth } from "./auth/AuthProvider";
import type { Role } from "./api/types";
import { AppLayout } from "./components/AppLayout";
import { EmptyState, LoadingState } from "./components/common";
import { AdminsPage } from "./pages/AdminsPage";
import { AuditPage } from "./pages/AuditPage";
import { ClientDetailPage } from "./pages/ClientDetailPage";
import { ClientsPage } from "./pages/ClientsPage";
import { DashboardPage } from "./pages/DashboardPage";
import { LeadDetailPage } from "./pages/LeadDetailPage";
import { LeadsPage } from "./pages/LeadsPage";
import { LoginPage } from "./pages/LoginPage";
import { MarketsPage } from "./pages/MarketsPage";
import { OutcomesPage } from "./pages/OutcomesPage";
import { SettingsPage } from "./pages/SettingsPage";
import { SignalsPage } from "./pages/SignalsPage";
import { SourcesPage } from "./pages/SourcesPage";

/**
 * Route guard. This is convenience, not security: every endpoint behind these
 * screens re-checks the session and the role server-side, because a client-side
 * gate is a suggestion.
 */
function Protected({ children, needs }: { children: ReactNode; needs?: Role }) {
  const { user, can } = useAuth();
  const location = useLocation();

  if (user === undefined) {
    return (
      <Box sx={{ p: 4 }}>
        <LoadingState label="Restoring session…" />
      </Box>
    );
  }
  if (user === null) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (needs && !can(needs)) {
    return (
      <EmptyState
        title="You do not have access to this screen"
        hint={`It requires the "${needs}" role. Ask an owner to change yours.`}
      />
    );
  }
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <Protected>
            <AppLayout />
          </Protected>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="/leads" element={<LeadsPage />} />
        <Route path="/leads/:id" element={<LeadDetailPage />} />
        <Route path="/outcomes" element={<OutcomesPage />} />
        <Route path="/sources" element={<SourcesPage />} />
        <Route path="/signals" element={<SignalsPage />} />
        <Route path="/markets" element={<MarketsPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/clients/:id" element={<ClientDetailPage />} />
        {/* Readable by any role: the connected sender explains where a client's
            mail comes from. The connect/disconnect buttons on it are owner-gated,
            and so are the routes behind them. */}
        <Route path="/settings" element={<SettingsPage />} />
        <Route
          path="/audit"
          element={
            <Protected needs="operator">
              <AuditPage />
            </Protected>
          }
        />
        <Route
          path="/admins"
          element={
            <Protected needs="owner">
              <AdminsPage />
            </Protected>
          }
        />
        <Route
          path="*"
          element={<EmptyState title="Not found" hint="That screen does not exist in this console." />}
        />
      </Route>
    </Routes>
  );
}
